(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.DSProductCart = factory(root);
})(typeof window === 'object' ? window : globalThis, function createProductCart(env) {
  'use strict';
  let pending = null;
  let phase = 'idle';
  let uncertain = false;
  let groupSequence = 0;
  const cartUrl = () => (env.Shopify?.routes?.root || '/') + 'cart';
  const error = (message, code, extra = {}) => Object.assign(new Error(message), { code, ...extra });
  const report = next => {
    phase = next;
    if (env.CustomEvent) env.document.dispatchEvent(new env.CustomEvent('ds:cart-state', { detail: { phase } }));
  };
  const variantId = selection => String(selection?.variantId || '');
  function isAddEndpoint(input, init) {
    try {
      const url = new env.URL(typeof input === 'string' ? input : input.url, env.location.href);
      return url.origin === env.location.origin && /\/cart\/add(?:\.js)?$/.test(url.pathname) && String(init?.method || input?.method || 'GET').toUpperCase() === 'POST';
    } catch { return false; }
  }
  function isAddRequest(input, init, selectedId) {
    if (!isAddEndpoint(input, init)) return false;
    try {
      const body = init?.body;
      if (typeof body === 'string') {
        const data = JSON.parse(body);
        return (data.items || [data]).some(item => String(item.id) === selectedId);
      }
      return body && typeof body.get === 'function' && String(body.get('id')) === selectedId;
    } catch { return false; }
  }
  function engravingSnapshot(selection) {
    const selected = env.DSEngraving?.getSelection?.();
    if (!selected) {
      if (selection.engravingText) throw error('Your engraving is still loading. Please try again.', 'ENGRAVING_NOT_READY');
      return null;
    }
    if (!selected.valid) throw error(selected.error || 'Check your engraving text.', 'INVALID_ENGRAVING');
    if (!selected.characters && !String(selected.text || '').trim()) return null;
    const text = String(selected.text || '').normalize('NFC');
    const count = Array.from(new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text)).filter(part => !/^\s+$/u.test(part.segment)).length;
    if (!text || Array.from(text).length > 200 || /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/u.test(text) ||
        !Number.isInteger(count) || count < 1 || count !== selected.characters || Number(selected.unitPriceCents) !== 1000 ||
        Number(selected.costCents) !== count * 1000 || !/^\d{8,20}$/.test(String(selected.variantId || '')) ||
        !Number.isSafeInteger(Number(selected.variantId)) || !Number.isSafeInteger(Number(variantId(selection))) || String(selected.variantId) === variantId(selection)) throw error('Your engraving price could not be verified. Please check the text and try again.', 'INVALID_ENGRAVING');
    if (selection.engravingText !== undefined && selection.engravingText !== text ||
        selection.engravingCharacters !== undefined && selection.engravingCharacters !== count ||
        selection.engravingCents !== undefined && selection.engravingCents !== count * 1000) throw error('Your engraving selection is updating. Please try again.', 'SELECTION_NOT_SYNCED');
    return { text, characters: count, variantId: String(selected.variantId) };
  }
  function payloadFromBody(body) {
    if (typeof body === 'string') {
      const data = JSON.parse(body);
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw error('The product options could not be prepared.', 'INVALID_CART_PAYLOAD');
      if (Array.isArray(data.items)) return data;
      const { id, quantity, properties, selling_plan, ...extras } = data;
      return { ...extras, items: [{ id, quantity: quantity || 1, ...(properties ? { properties } : {}), ...(selling_plan ? { selling_plan } : {}) }] };
    }
    if (!body || typeof body.entries !== 'function') throw error('The product options could not be prepared. Please refresh and try again.', 'INVALID_CART_PAYLOAD');
    const item = { properties: {} };
    const data = { items: [item] };
    for (const [name, value] of body.entries()) {
      if (typeof value !== 'string') throw error('This custom option needs studio assistance.', 'UNSUPPORTED_CART_OPTION');
      const property = /^properties\[(.+)\]$/.exec(name);
      if (property) item.properties[property[1]] = value;
      else if (['id', 'quantity', 'selling_plan'].includes(name)) item[name] = value;
      else if (!['form_type', 'utf8', 'product-id', 'section-id'].includes(name)) data[name] = value;
    }
    item.quantity = Number(item.quantity ?? 1);
    return data;
  }
  function withEngraving(data, selectedId, engraving) {
    if (!Array.isArray(data.items)) throw error('Your selected piece could not be verified.', 'INVALID_CART_PAYLOAD');
    const parents = data.items.filter(item => String(item.id) === selectedId);
    if (parents.length !== 1 || data.items.some(item => String(item.id) === engraving.variantId)) throw error('Your engraving could not be attached to this piece. Please refresh and try again.', 'ENGRAVING_PAYLOAD_CONFLICT');
    const parent = parents[0], quantity = Number(parent.quantity || 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(quantity * engraving.characters)) throw error('The selected quantity could not be verified.', 'INVALID_CART_QUANTITY');
    const properties = parent.properties || {};
    const group = String(properties._gpo_product_group || ('ds-eng-' + (env.crypto?.randomUUID?.() || Date.now() + '-' + (++groupSequence) + '-' + Math.random().toString(36).slice(2))));
    parent.properties = { ...properties, Engraving: engraving.text, _ds_engraving_chars: String(engraving.characters), _ds_engraving_variant: engraving.variantId, _gpo_product_group: group };
    data.items.push({
      id: Number(engraving.variantId), quantity: engraving.characters * quantity, parent_id: Number(selectedId),
      properties: { Engraving: engraving.text, _gpo_parent_product_group: group, _gpo_per_character: String(engraving.characters), _gpo_field_name: 'Engraving' }
    });
    return data;
  }
  function jsonInit(input, init, data) {
    const headers = new (env.Headers || Headers)(init?.headers || (typeof input === 'object' ? input?.headers : undefined));
    headers.set('Content-Type', 'application/json');
    return { ...init, headers, body: JSON.stringify(data) };
  }
  function add(selection) {
    if (pending) return pending.promise;
    if (uncertain) return Promise.reject(error('Check your cart before trying again. The previous addition could not be confirmed.', 'CART_UNCERTAIN', { uncertain: true, cartUrl: cartUrl() }));
    const form = env.document.getElementById('ds-mapped-product-form');
    if (!form || !variantId(selection) || selection.available === false) return Promise.reject(error('Choose an available product option.', 'INVALID_SELECTION'));
    const config = env.DS_PREVIEW_CONFIG || {};
    const variant = (config.variants || []).find(item => String(item.id) === variantId(selection));
    if (!variant || variant.available === false) return Promise.reject(error('This product option is unavailable. Please choose another option.', 'INVALID_VARIANT'));
    const readiness = env.DSProductOptions?.sync(selection);
    if (!readiness?.appReady) return Promise.reject(error('Complete the product options before adding this piece.', 'OPTIONS_NOT_READY'));
    let engraving;
    try { engraving = engravingSnapshot(selection); } catch (failure) { return Promise.reject(failure); }
    const ringSize = env.document.getElementById('ring-size');
    if (ringSize && !String(ringSize.value || '').trim()) return Promise.reject(error('Choose your ring size.', 'RING_SIZE_REQUIRED'));
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity?.();
      return Promise.reject(error('Check the required product options.', 'INVALID_OPTIONS'));
    }
    const id = form.querySelector('[name="id"]');
    if (!id || String(id.value) !== variantId(selection)) return Promise.reject(error('Your selection is updating. Please try again.', 'SELECTION_NOT_SYNCED'));
    const clone = form.querySelector('.gpo-clone-atc-button');
    if (readiness.hasPaidOptions && (!clone || clone.disabled)) return Promise.reject(error('The option price is still being prepared. Please try again shortly.', 'GPO_NOT_READY'));
    if (clone?.disabled) return Promise.reject(error('The product options are still being prepared.', 'GPO_NOT_READY'));

    let resolve, reject;
    const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    const attempt = { promise, requestStarted: false, native: !!clone, originalFetch: env.fetch, wrapper: null, timer: null, expectedItems: [] };
    pending = attempt;
    report('adding');
    const finish = (failure, result) => {
      if (pending !== attempt) return;
      env.clearTimeout(attempt.timer);
      if (attempt.wrapper && env.fetch === attempt.wrapper) env.fetch = attempt.originalFetch;
      pending = null;
      if (failure) { uncertain = !!failure.uncertain; report(uncertain ? 'uncertain' : 'error'); reject(failure); }
      else { report('added'); resolve(result); }
    };
    const unknown = () => error('We could not confirm the complete cart update. Check your cart before adding this piece again.', 'CART_UNCERTAIN', { uncertain: true, cartUrl: cartUrl() });
    const inspect = async response => {
      let data;
      try { data = await response.clone().json(); }
      catch { finish(unknown()); return response; }
      if (!response.ok || Number(data?.status) >= 400) {
        const status = Number(data?.status || response.status);
        // Shopify may partially add available inventory, even with a 422 response.
        finish(attempt.expectedItems.length > 1 ? unknown() : status >= 400 && status < 500
          ? error(String(data?.description || data?.message || 'This item could not be added. Review your options and try again.'), 'CART_REJECTED', { status })
          : unknown());
        return response;
      }
      const items = Array.isArray(data?.items) ? data.items : [data];
      const expected = attempt.expectedItems.length ? attempt.expectedItems : [{ id: variantId(selection), quantity: 1 }];
      let complete = expected.every(expectedItem => items.some(item => String(item?.variant_id || item?.id) === String(expectedItem.id) && Number(item.quantity ?? 1) >= Number(expectedItem.quantity || 1)));
      if (complete && engraving) {
        const parent = items.find(item => String(item?.variant_id || item?.id) === variantId(selection));
        const fee = items.find(item => String(item?.variant_id || item?.id) === engraving.variantId);
        const sentParent = expected.find(item => String(item.id) === variantId(selection));
        const group = sentParent.properties._gpo_product_group;
        complete = !!parent?.key && fee?.parent_relationship?.parent_key === parent.key &&
          parent.properties?.Engraving === engraving.text && parent.properties?._ds_engraving_chars === String(engraving.characters) &&
          parent.properties?._ds_engraving_variant === engraving.variantId && parent.properties?._gpo_product_group === group &&
          fee.properties?.Engraving === engraving.text && fee.properties?._gpo_parent_product_group === group &&
          fee.properties?._gpo_per_character === String(engraving.characters) &&
          Number(fee.original_price ?? fee.price) === 1000 &&
          Number(fee.quantity) === Number(parent.quantity) * engraving.characters;
      }
      if (!complete) finish(unknown());
      else finish(null, { items, cartUrl: cartUrl(), nativeGpo: attempt.native });
      return response;
    };
    attempt.timer = env.setTimeout(() => finish(unknown()), 20000);
    if (clone) {
      // GPO still prepares its own paid options; attach engraving to the same request.
      attempt.wrapper = function (input, init) {
        const matching = pending === attempt && !attempt.requestStarted && isAddRequest(input, init, variantId(selection));
        if (engraving && pending === attempt && !attempt.requestStarted && isAddEndpoint(input, init) && !matching) {
          try {
            const other = payloadFromBody(init?.body);
            if (!other.items.length || other.items.some(item => !item.id)) throw error('The product options could not be prepared with engraving. Please refresh and try again.', 'INVALID_CART_PAYLOAD');
          } catch (failure) { finish(failure); return Promise.reject(failure); }
        }
        if (matching) {
          try {
            if (engraving || typeof init?.body === 'string') {
              let data = payloadFromBody(init.body);
              const missing = (readiness.addonVariantIds || []).some(id => !data.items.some(item => String(item.id) === String(id)));
              if (missing) throw error('The option upgrade could not be prepared. Refresh the page and select your options again.', 'GPO_PAYLOAD_INCOMPLETE');
              if (engraving) { data = withEngraving(data, variantId(selection), engraving); init = jsonInit(input, init, data); }
              attempt.expectedItems = data.items;
            }
            attempt.requestStarted = true;
          } catch (failure) { finish(failure); return Promise.reject(failure); }
        }
        let response;
        try { response = attempt.originalFetch.call(this, input, init); }
        catch (failure) { if (matching) finish(unknown()); throw failure; }
        return matching ? Promise.resolve(response).then(inspect, failure => { finish(unknown()); throw failure; }) : response;
      };
      env.fetch = attempt.wrapper;
      try { clone.click(); } catch { finish(unknown()); }
    } else {
      // Free-option products keep their complete form; convert only when a fee is added.
      let init;
      try {
        init = { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin', body: new env.FormData(form) };
        if (engraving) {
          const data = withEngraving(payloadFromBody(init.body), variantId(selection), engraving);
          attempt.expectedItems = data.items;
          init = jsonInit(null, init, data);
        }
      } catch (failure) { finish(failure); return promise; }
      attempt.requestStarted = true;
      try {
        Promise.resolve(attempt.originalFetch.call(env, (env.Shopify?.routes?.root || '/') + 'cart/add.js', init)).then(inspect, () => finish(unknown()));
      } catch { finish(unknown()); }
    }
    return promise;
  }
  return { add, get state() { return { phase, busy: !!pending, uncertain, cartUrl: cartUrl() }; } };
});

